import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import './topbar.css';

const TOPBAR_HEIGHT = 50;
const SCROLL_THRESHOLD = 8;

function TopBar() {
  const promoTexts = useSelector((state) => state.homeState.promoTexts);
  const announcements = useMemo(
    () =>
      (promoTexts || []).filter(
        (text) => typeof text === 'string' && text.trim()
      ),
    [promoTexts]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (!announcements.length) return undefined;

    const text = announcements[currentIndex] || '';
    let charIndex = 0;
    let isDeleting = false;

    const TYPING_SPEED = 90;
    const DELETING_SPEED = 45;
    const PAUSE_AFTER_TYPED_MS = 1200;
    const PAUSE_AFTER_DELETED_MS = 150;

    let timeoutId = null;

    const tick = () => {
      if (!isDeleting) {
        const next = text.slice(0, charIndex + 1);
        setDisplayText(next);
        charIndex += 1;

        if (charIndex >= text.length) {
          isDeleting = true;
          timeoutId = setTimeout(tick, PAUSE_AFTER_TYPED_MS);
          return;
        }

        timeoutId = setTimeout(tick, TYPING_SPEED);
        return;
      }

      const next = text.slice(0, Math.max(0, charIndex - 1));
      setDisplayText(next);
      charIndex = Math.max(0, charIndex - 1);

      if (charIndex === 0) {
        timeoutId = setTimeout(() => {
          setCurrentIndex((prev) =>
            prev === announcements.length - 1 ? 0 : prev + 1
          );
        }, PAUSE_AFTER_DELETED_MS);
        return;
      }

      timeoutId = setTimeout(tick, DELETING_SPEED);
    };

    tick();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentIndex, announcements]);

  useEffect(() => {
    if (!announcements.length) {
      document.documentElement.style.setProperty('--topbar-offset', '0px');
      document.documentElement.style.setProperty('--topbar-height', '0px');
      return undefined;
    }

    const getScrollTop = () => {
      const root = document.getElementById('root');
      return Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0,
        root?.scrollTop || 0
      );
    };

    const update = () => {
      const hidden = getScrollTop() > SCROLL_THRESHOLD;
      setIsHidden(hidden);
      document.documentElement.classList.toggle('topbar-scrolled', hidden);
      document.documentElement.style.setProperty(
        '--topbar-offset',
        hidden ? '0px' : `${TOPBAR_HEIGHT}px`
      );
      document.documentElement.style.setProperty(
        '--topbar-height',
        `${TOPBAR_HEIGHT}px`
      );
    };

    update();

    const scrollTargets = [
      window,
      document,
      document.documentElement,
      document.body,
      document.getElementById('root'),
    ].filter(Boolean);

    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', update, { passive: true });
    });

    window.addEventListener('wheel', update, { passive: true });
    window.addEventListener('touchmove', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', update);
      });
      window.removeEventListener('wheel', update);
      window.removeEventListener('touchmove', update);
      window.removeEventListener('resize', update);
      document.documentElement.classList.remove('topbar-scrolled');
    };
  }, [announcements.length]);

  if (!announcements.length) {
    return null;
  }

  return (
    <>
      <div className={`topbar${isHidden ? " topbar--hidden" : ""}`}>
        <div className="topbar-text">{displayText}</div>
      </div>
      <div
        className="topbar-spacer"
        style={{ height: isHidden ? 0 : TOPBAR_HEIGHT }}
        aria-hidden="true"
      />
    </>
  );
}

export { TOPBAR_HEIGHT, SCROLL_THRESHOLD };
export default TopBar;
